/**
 * Tag Editor Content Service
 * Manages editor content loading, parsing, and normalization
 */

import { Injectable } from '@angular/core'
import { COMPONENT_STRINGS, DEBUG_PREVIEW_LENGTHS, TAG_EDITOR_DELAY, TAG_EDITOR_STRINGS } from '../tag-editor.constants'
import { TagManagerService } from './tag-manager.service'
import { TagEditorStateService } from './tag-editor-state.service'
import { DebugLoggingService } from './debug-logging.service'
import { InsertedTagCustom } from '../tag-editor.model'

/**
 * Options for loading content
 */
export interface LoadOptions {
  readonly debug?: boolean
  readonly parseTags?: boolean
  readonly emitChange?: boolean
}

/**
 * Callbacks for content operations
 */
export interface ContentCallbacks {
  readonly onContentLoaded: (content: string) => void
  readonly onTagsUpdated: (tags: ReadonlyArray<InsertedTagCustom>) => void
  readonly onMarkForCheck: () => void
  readonly onEmitChange: (content: string) => void
}

@Injectable({ providedIn: 'root' })
export class TagEditorContentService {
  constructor(
    private readonly tagManagerService: TagManagerService,
    private readonly stateService: TagEditorStateService,
    private readonly debugLoggingService: DebugLoggingService,
  ) {}

  private debugLog(message: string, ...args: ReadonlyArray<unknown>): void {
    this.debugLoggingService.logWithService(COMPONENT_STRINGS.COMPONENT_NAME, message, ...args)
  }

  /**
   * Loads content into the editor and parses existing tags
   */
  public loadContent(
    editor: HTMLElement,
    content: string,
    callbacks: ContentCallbacks,
    options: LoadOptions = {},
  ): void {
    const { debug = false, parseTags = true, emitChange = true } = options

    if (!content || content.trim() === '' || content === TAG_EDITOR_STRINGS.EMPTY_PARAGRAPH) {
      this.debugLog(' Cannot load content - empty or placeholder:', {
        hasContent: !!content,
        contentLength: content?.length || 0,
        isEmpty: content === TAG_EDITOR_STRINGS.EMPTY_PARAGRAPH,
      })
      editor.innerHTML = TAG_EDITOR_STRINGS.EMPTY_PARAGRAPH
      if (parseTags) {
        const tags = this.parseTagsFromEditor(editor, callbacks)
        callbacks.onTagsUpdated(tags)
      }
      return
    }

    this.stateService.setLoadingContent(true)
    this.debugLog(' Loading content into editor:', {
      contentLength: content.length,
      contentPreview: content.substring(0, DEBUG_PREVIEW_LENGTHS.CONTENT_PREVIEW_MEDIUM),
    })

    if (debug) {
      this.debugLog(' DEBUG: Full HTML content being loaded:')
      this.debugLog(content)
      this.debugLog(' DEBUG: HTML content ends here')
    }

    const extractedTags: ReadonlyArray<{
      readonly id: string
      readonly label: string
      readonly text: string
      readonly color: string
    }> = this.tagManagerService.extractTagDataFromHTML(content)
    this.debugLog('📦 Extracted tags:', extractedTags.length)

    try {
      editor.innerHTML = content

      const actualContent: string = editor.innerHTML
      if (actualContent.trim() === '' && content.trim() !== '') {
        this.debugLog(' WARNING: Content was not set correctly, retrying...')
        setTimeout(() => {
          editor.innerHTML = content
          this.debugLog(' Retry: Content set, length:', editor.innerHTML.length)
        }, DEBUG_PREVIEW_LENGTHS.RETRY_DELAY_MS)
      }

      if (debug) {
        this.debugLog(' DEBUG: DOM content after innerHTML:')
        this.debugLog(editor.innerHTML.substring(0, DEBUG_PREVIEW_LENGTHS.CONTENT_PREVIEW_LONG))
        this.debugLog(' DEBUG: DOM content length:', editor.innerHTML.length)
        this.debugLog(' DEBUG: HTML content ends here')
      }
    } catch (error: unknown) {
      this.debugLog(' ERROR: Failed to set innerHTML:', error)
      this.stateService.setLoadingContent(false)
      return
    }

    setTimeout(() => {
      const currentEditorContent: string = editor.innerHTML
      if (currentEditorContent.trim() === '' && content.trim() !== '') {
        this.debugLog(' WARNING: Content was cleared from editor, restoring...')
        editor.innerHTML = content
      }

      if (extractedTags.length > 0) {
        const restoredCount: number = this.tagManagerService.restoreTagAttributes(editor, extractedTags)
        this.debugLog(' Restored attributes for', restoredCount, 'tags')
      } else {
        this.debugLog('🔧 No extracted tags found - tags should be preserved via data-tag-id attributes')
      }

      const normalizedCount: number = this.tagManagerService.normalizeTagStructure(editor)
      if (normalizedCount > 0) {
        this.debugLog(' Normalized tag structures after load:', normalizedCount)
      }

      if (parseTags) {
        const tags = this.parseTagsFromEditor(editor, callbacks)
        callbacks.onTagsUpdated(tags)
      }

      this.tagManagerService.resetTagsToNonEditable(editor)

      this.stateService.setLoadingContent(false)

      const finalContent: string = this.getContent(editor)
      this.debugLog(' Content loaded successfully:', {
        finalContentLength: finalContent.length,
        hasContent: finalContent.trim() !== TAG_EDITOR_STRINGS.EMPTY_PARAGRAPH,
      })

      callbacks.onMarkForCheck()

      if (emitChange) {
        callbacks.onEmitChange(finalContent)
      }
    }, TAG_EDITOR_DELAY.CONTENT_LOAD_DELAY_MS)
  }

  /**
   * Gets HTML content from editor
   */
  public getContent(editor: HTMLElement): string {
    return editor.innerHTML
  }

  /**
   * Checks if content has changed
   */
  public hasContentChanged(editor: HTMLElement, previousContent: string): boolean {
    const currentContent: string = this.getContent(editor)
    return currentContent !== previousContent
  }

  /**
   * Parses tags from editor content and updates state
   */
  public parseTagsFromEditor(editor: HTMLElement, _callbacks: ContentCallbacks): ReadonlyArray<InsertedTagCustom> {
    const tags: ReadonlyArray<InsertedTagCustom> = this.tagManagerService.parseTagsFromContent(editor)
    this.debugLog(' Parsed tags from content:', tags.length)
    return tags
  }
}
